import * as setup from '@/setup';
import Button from '@/objects/button';
import Title from '@/objects/title';
import Header from '@/objects/header';
import { HEADER_HEIGHT } from '@/constants';
import runtime from '@/runtime';
import axios from 'axios';
import * as shareUtils from '@/utils/share';
import * as audio from '@/utils/audio';

/**
 * Costanti per il layout della lista scrollabile
 */
var ROW_HEIGHT = 70;
var SCROLL_PADDING = 20;
var TABLE_WIDTH_RATIO = 0.9;

export default class ChallengeSelection extends Phaser.Scene {
	constructor() {
		super({key: 'ChallengeSelection'});
		this.friendsList = [];
		this.selectedFriend = null;
		this.loadingText = null;
		this.errorText = null;
		/** @type {Phaser.GameObjects.Container|null} */
		this.scrollContainer = null;
		/** @type {Phaser.GameObjects.Graphics|null} */
		this.scrollMask = null;
		/** @type {number} */
		this.scrollY = 0;
		/** @type {number} */
		this.maxScrollY = 0;
		/** @type {number} */
		this.minScrollY = 0;
		/** @type {boolean} */
		this.isDragging = false;
		/** @type {number} */
		this.dragStartY = 0;
		/** @type {number} */
		this.scrollStartY = 0;
		/** @type {HTMLInputElement|null} */
		this.searchInputElement = null;
		/** @type {string} */
		this.searchValue = '';
		/** @type {boolean} */
		this.isSearchMode = false;
	}

	create() {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;

		// Registra evento shutdown per pulire l'input HTML quando si cambia scena
		this.events.once('shutdown', this.cleanupInputs, this);

		// Sfondo del gioco (stesso del Game scene)
		this.add
			.image(centerX, height / 2, 'background')
			.setOrigin(0.5, 0.5);

		// Header del gioco (logo, sfondo logo, ditloide)
		this.createGameHeader(centerX);

		// Bottone "Invita un amico" - posizionato dopo il ditloide
		this.createInviteButton(centerX);

		// Testo di caricamento
		this.loadingText = this.add.text(
			centerX,
			height / 2,
			'Caricamento amici...',
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FFFFFF'}
		).setOrigin(0.5);

		// Carica lista amici
		this.loadFriends();
	}

	/**
	 * Pulisce gli input HTML DOM quando si lascia la scena
	 */
	cleanupInputs() {
		if (this.searchInputElement) {
			document.body.removeChild(this.searchInputElement);
			this.searchInputElement = null;
		}
	}

	/**
	 * Chiamato quando la scena viene distrutta
	 */
	destroy() {
		this.cleanupInputs();
		super.destroy();
	}

	/**
	 * Crea l'header della scena con il pulsante indietro e il titolo.
	 *
	 * @param {number} centerX - Posizione X centrale.
	 */
	createGameHeader(centerX) {
		const headerY = HEADER_HEIGHT / 2;

		// Header con componente riutilizzabile
		new Header(this, 'SFIDA UN AMICO');

		// Pulsante back (icona)
		const backArrow = this.add.image(40, headerY, 'back-icon');
		backArrow.setOrigin(0, 0.5);
		backArrow.setInteractive({ useHandCursor: true });

		backArrow.on('pointerup', () => {
			audio.playSound(this, 'button-click');
			this.cleanupInputs();
			this.scene.start('Challenge');
		});
	}

	loadFriends() {
		// Usa il tipo di autenticazione determinato al login per decidere come caricare gli amici
		// Se l'utente è loggato con Facebook, usa FBInstant API
		// Se l'utente è loggato con email, carica dal database

		if (runtime.authType === 'facebook') {
			// Utente loggato con Facebook: usa Facebook Instant Games API
			this.loadFriendsFromFacebook();
		} else {
			// Utente loggato con email o tipo non determinato: carica dal database
			this.loadFriendsFromDatabase();
		}
	}

	loadFriendsFromFacebook() {
		// Verifica se FBInstant è disponibile e inizializzato correttamente
		const isLocalhost = window.location.hostname === 'localhost' ||
		                    window.location.hostname === '127.0.0.1' ||
		                    window.location.hostname.includes('.lndo.site');

		const isFBInstantAvailable = !isLocalhost &&
		                              typeof FBInstant !== 'undefined' &&
		                              FBInstant &&
		                              FBInstant.context &&
		                              typeof FBInstant.getSDKVersion === 'function';

		if (!isFBInstantAvailable) {
			console.warn('FBInstant non disponibile, fallback al database');
			this.loadFriendsFromDatabase();
			return;
		}

		// Usa Facebook Instant Games API
		FBInstant.context.chooseAsync({
			filters: ['INVITABLE'],
			maxSize: 50,
			minSize: 1
		}).then((players) => {
			this.loadingText.destroy();
			if (players && players.length > 0) {
				this.displayFriends(players);
			} else {
				this.showError('Nessun amico disponibile per la sfida');
			}
		}).catch((error) => {
			console.error('Errore selezione amici Facebook:', error);
			this.loadingText.destroy();
			// In caso di errore con FBInstant, fallback al database
			this.loadFriendsFromDatabase();
		});
	}

	loadFriendsFromDatabase() {
		// Carica lista utenti dal database
		axios.get('/api/invites/available-friends')
			.then((response) => {
				this.loadingText.destroy();

				// Crea sempre il form di ricerca
				this.createSearchForm();

				if (response.data && response.data.length > 0) {
					this.displayFriendsFromDatabase(response.data);
				} else {
					// Mostra messaggio ma permette comunque la ricerca
					this.showNoFriendsMessage();
				}
			})
			.catch((error) => {
				console.error('Errore caricamento utenti:', error);
				this.loadingText.destroy();
				// Crea comunque il form di ricerca
				this.createSearchForm();
				this.showError('Errore nel caricamento degli utenti');
			});
	}

	/**
	 * Mostra messaggio quando non ci sono amici
	 */
	showNoFriendsMessage() {
		const width = this.sys.game.config.width;
		const centerX = width / 2;
		const scrollAreaTop = HEADER_HEIGHT + 250;

		this.noFriendsText = this.add.text(
			centerX,
			scrollAreaTop + 50,
			'Nessun amico trovato.\nUsa la ricerca per trovare nuovi avversari!',
			{
				...setup.styles.gameOver.greetingStyle,
				fontSize: 20,
				fill: '#666666',
				align: 'center'
			}
		).setOrigin(0.5);
	}

	/**
	 * Crea il form di ricerca per trovare utenti per nickname
	 */
	createSearchForm() {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;

		// Posizione del form di ricerca: subito dopo il bottone "Invita un amico"
		const searchY = HEADER_HEIGHT + 150;

		// Crea l'input HTML per la ricerca
		this.createSearchInput(centerX, searchY);
	}

	/**
	 * Crea l'input HTML per la ricerca posizionato sopra il canvas
	 *
	 * @param {number} centerX - Coordinata X del centro
	 * @param {number} searchY - Coordinata Y della posizione
	 */
	createSearchInput(centerX, searchY) {
		// Rimuovi input precedente se presente
		if (this.searchInputElement) {
			document.body.removeChild(this.searchInputElement);
			this.searchInputElement = null;
		}

		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;

		// Calcola posizione input basandosi sul canvas
		const canvas = this.sys.game.canvas;
		const canvasRect = canvas.getBoundingClientRect();

		// Calcola il fattore di scala
		const scaleX = canvasRect.width / width;
		const scaleY = canvasRect.height / height;

		const inputWidth = Math.min(600, width - 50) * scaleX;
		const inputHeight = 80 * scaleY;

		// Calcola posizione centrata dell'input
		const inputLeft = canvasRect.left + (centerX * scaleX) - (inputWidth / 2);
		const inputTop = canvasRect.top + (searchY * scaleY) - (inputHeight / 2);

		// Crea input HTML
		const input = document.createElement('input');
		input.type = 'text';
		input.placeholder = 'Cerca per nickname...';
		input.style.position = 'fixed';
		input.style.left = inputLeft + 'px';
		input.style.top = inputTop + 'px';
		input.style.width = inputWidth + 'px';
		input.style.height = inputHeight + 'px';
		input.style.fontSize = Math.min(36, 32 * scaleX) + 'px';
		input.style.textAlign = 'center';
		input.style.border = '1.5px solid #4A90E2';
		input.style.borderRadius = '5px';
		input.style.padding = '5px 15px';
		input.style.boxSizing = 'border-box';
		input.style.zIndex = '1000';
		input.style.fontFamily = 'Arial';
		input.style.backgroundColor = '#ffffff';
		input.style.outline = 'none';

		input.addEventListener('input', (e) => {
			this.searchValue = e.target.value.trim();
		});

		input.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				input.blur();
				// Forza la chiusura della tastiera su mobile
				if (document.activeElement) {
					document.activeElement.blur();
				}
				if (this.searchValue.length >= 3) {
					this.searchUsers(this.searchValue);
				}
			}
		});

		document.body.appendChild(input);
		this.searchInputElement = input;

		// Listener per far perdere il focus all'input quando si clicca sul canvas
		this.input.on('pointerdown', () => {
			if (this.searchInputElement) {
				this.searchInputElement.blur();
			}
		});

		// Bottone per tornare agli amici (visibile solo in modalità ricerca)
		this.backToFriendsButton = this.add.text(centerX, searchY + 60, '← Torna agli amici', {
			fontSize: 28,
			fontFamily: 'Arial',
			fill: '#4A90E2'
		}).setOrigin(0.5).setInteractive({ useHandCursor: true }).setVisible(false);

		this.backToFriendsButton.on('pointerdown', () => {
			this.clearSearch();
		});
	}

	/**
	 * Cerca utenti per nickname
	 *
	 * @param {string} query - Testo da cercare
	 */
	searchUsers(query) {
		// Pulisci lista precedente
		this.clearList();

		// Mostra caricamento
		if (this.loadingText) {
			this.loadingText.destroy();
		}
		this.loadingText = this.add.text(
			this.sys.game.config.width / 2,
			this.sys.game.config.height / 2,
			'Ricerca in corso...',
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FFFFFF'}
		).setOrigin(0.5);

		axios.get('/api/invites/search-users', { params: { q: query } })
			.then((response) => {
				this.loadingText.destroy();
				this.isSearchMode = true;

				// Mostra bottone "Torna agli amici"
				if (this.backToFriendsButton) {
					this.backToFriendsButton.setVisible(true);
				}

				if (response.data && response.data.length > 0) {
					this.displayFriendsFromDatabase(response.data, true);
				} else {
					this.showSearchNoResults(query);
				}
			})
			.catch((error) => {
				console.error('Errore ricerca utenti:', error);
				this.loadingText.destroy();

				let errorMsg = 'Errore nella ricerca';
				if (error.response && error.response.data && error.response.data.message) {
					errorMsg = error.response.data.message;
				}
				this.showError(errorMsg);
			});
	}

	/**
	 * Mostra messaggio quando la ricerca non trova risultati
	 *
	 * @param {string} query - Testo cercato
	 */
	showSearchNoResults(query) {
		const width = this.sys.game.config.width;
		const centerX = width / 2;
		const scrollAreaTop = HEADER_HEIGHT + 250;

		this.noResultsText = this.add.text(
			centerX,
			scrollAreaTop + 50,
			'Nessun utente trovato per "' + query + '"',
			{
				...setup.styles.gameOver.greetingStyle,
				fontSize: 20,
				fill: '#666666',
				align: 'center'
			}
		).setOrigin(0.5);
	}

	/**
	 * Pulisce la lista corrente
	 */
	clearList() {
		// Rimuovi container scroll
		if (this.scrollContainer) {
			this.scrollContainer.destroy();
			this.scrollContainer = null;
		}

		// Rimuovi maschera
		if (this.scrollMask) {
			this.scrollMask.destroy();
			this.scrollMask = null;
		}

		// Rimuovi messaggio "nessun amico"
		if (this.noFriendsText) {
			this.noFriendsText.destroy();
			this.noFriendsText = null;
		}

		// Rimuovi messaggio "nessun risultato"
		if (this.noResultsText) {
			this.noResultsText.destroy();
			this.noResultsText = null;
		}

		// Reset scroll
		this.scrollY = 0;
	}

	/**
	 * Torna alla visualizzazione degli amici
	 */
	clearSearch() {
		this.isSearchMode = false;
		this.searchValue = '';

		// Pulisci input
		if (this.searchInputElement) {
			this.searchInputElement.value = '';
		}

		// Nascondi bottone "Torna agli amici"
		if (this.backToFriendsButton) {
			this.backToFriendsButton.setVisible(false);
		}

		// Pulisci lista
		this.clearList();

		// Ricarica amici
		this.loadingText = this.add.text(
			this.sys.game.config.width / 2,
			this.sys.game.config.height / 2,
			'Caricamento amici...',
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FFFFFF'}
		).setOrigin(0.5);

		axios.get('/api/invites/available-friends')
			.then((response) => {
				this.loadingText.destroy();
				if (response.data && response.data.length > 0) {
					this.displayFriendsFromDatabase(response.data);
				} else {
					this.showNoFriendsMessage();
				}
			})
			.catch((error) => {
				console.error('Errore caricamento utenti:', error);
				this.loadingText.destroy();
				this.showError('Errore nel caricamento degli utenti');
			});
	}

	displayFriends(friends) {
		const width = this.sys.game.config.width;
		const centerX = width / 2;
		// Inizia dopo l'header del gioco
		const startY = HEADER_HEIGHT + 150;
		const rowHeight = 70;
		const padding = 20;
		const tableWidth = width * 0.9;
		const nameColumnWidth = tableWidth * 0.7;
		const buttonColumnWidth = tableWidth * 0.3;

		// Crea la tabella degli amici
		friends.forEach((friend, index) => {
			const y = startY + (index * rowHeight);

			// Evita overflow
			if (y + rowHeight > this.sys.game.config.height - 100) {
				return;
			}

			// Riga della tabella
			const rowY = y - rowHeight / 2;
			const rowHeightActual = rowHeight - 5;
			const rowX = centerX - tableWidth / 2;

			const rowGraphics = this.add.graphics();
			rowGraphics.fillStyle(0xFFFFFF, 1);
			rowGraphics.fillRoundedRect(
				rowX,
				rowY,
				tableWidth,
				rowHeightActual,
				5
			);
			rowGraphics.lineStyle(1, 0xE0E0E0, 1);
			rowGraphics.strokeRoundedRect(
				rowX,
				rowY,
				tableWidth,
				rowHeightActual,
				5
			);

			// Avatar
			const avatarSize = 45;
			const avatarX = rowX + padding + avatarSize / 2;
			const avatar = this.add.circle(avatarX, y, avatarSize / 2, 0xD3D3D3);

			const faceIcon = this.add.text(avatarX, y, '😉', {
				fontSize: avatarSize * 0.7
			}).setOrigin(0.5);

			// Nome amico
			const friendName = friend.getName() || `Amico ${index + 1}`;
			const nameText = this.add.text(
				avatarX + avatarSize / 2 + 15,
				y,
				friendName.toUpperCase(),
				{
					fontSize: 18,
					fontFamily: 'Arial',
					fill: '#333333',
					fontStyle: 'bold'
				}
			).setOrigin(0, 0.5);

			// Bottone "Sfida" rosso - allineato perfettamente a destra e copre tutta l'altezza
			const buttonWidth = buttonColumnWidth;
			const buttonHeight = rowHeightActual; // Stessa altezza dello sfondo bianco
			// Calcola la posizione X: il bordo destro dello sfondo è rowX + tableWidth
			// Il centro del bottone deve essere a: bordo destro - metà larghezza
			const buttonX = rowX + tableWidth - buttonWidth / 2;

			// Crea il bottone con graphics per controllo preciso dei bordi
			const buttonGraphics = this.add.graphics();
			buttonGraphics.fillStyle(setup.colors.red, 1);
			// Crea un rettangolo arrotondato che copre perfettamente l'area destra
			// Il bottone inizia da rowX + tableWidth - buttonWidth e va fino a rowX + tableWidth
			buttonGraphics.fillRoundedRect(
				rowX + tableWidth - buttonWidth,
				rowY,
				buttonWidth,
				rowHeightActual,
				5 // Stesso raggio di arrotondamento dello sfondo
			);
			buttonGraphics.setInteractive({
				hitArea: new Phaser.Geom.Rectangle(
					rowX + tableWidth - buttonWidth,
					rowY,
					buttonWidth,
					rowHeightActual
				),
				hitAreaCallback: Phaser.Geom.Rectangle.Contains,
				useHandCursor: true
			});

			const buttonText = this.add.text(buttonX, y, 'Sfida', {
				fontSize: 24,
				fontFamily: 'Luckiest Guy',
				fill: '#FFFFFF',
				fontStyle: 'bold'
			}).setOrigin(0.5);
			buttonText.setShadow(1, 1, '#000000', 2, true, true);

			buttonGraphics.on('pointerdown', () => {
				this.createChallenge(friend.getID());
			});
		});
	}

	/**
	 * Visualizza la lista degli amici dal database con scroll verticale
	 *
	 * @param {Array} users - Lista degli utenti da visualizzare
	 */
	displayFriendsFromDatabase(users) {
		const width = this.sys.game.config.width;
		const height = this.sys.game.config.height;
		const centerX = width / 2;

		// Area scrollabile: da dopo il form di ricerca fino al fondo
		const scrollAreaTop = HEADER_HEIGHT + 250;
		const scrollAreaBottom = height - 20;
		const scrollAreaHeight = scrollAreaBottom - scrollAreaTop;

		var rowHeight = ROW_HEIGHT;
		var padding = SCROLL_PADDING;
		var tableWidth = width * TABLE_WIDTH_RATIO;
		const buttonColumnWidth = tableWidth * 0.3;

		// Se non ci sono utenti, mostra un messaggio
		if (users.length === 0) {
			this.add.text(
				centerX,
				scrollAreaTop + 50,
				'Nessun amico disponibile',
				{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#666666'}
			).setOrigin(0.5);
			return;
		}

		// Calcola altezza totale del contenuto
		const contentHeight = users.length * rowHeight;

		// Crea il container scrollabile
		this.scrollContainer = this.add.container(0, scrollAreaTop);

		// Crea la maschera per l'area visibile
		this.scrollMask = this.make.graphics();
		this.scrollMask.fillStyle(0xffffff);
		this.scrollMask.fillRect(0, scrollAreaTop, width, scrollAreaHeight);

		const mask = this.scrollMask.createGeometryMask();
		this.scrollContainer.setMask(mask);

		// Calcola i limiti dello scroll
		this.minScrollY = 0;
		this.maxScrollY = Math.max(0, contentHeight - scrollAreaHeight);
		this.scrollY = 0;

		// Crea le righe della tabella all'interno del container
		users.forEach((user, index) => {
			const y = index * rowHeight + rowHeight / 2;
			const rowY = index * rowHeight;
			const rowHeightActual = rowHeight - 5;
			const rowX = centerX - tableWidth / 2;

			// Sfondo bianco della riga
			const rowGraphics = this.add.graphics();
			rowGraphics.fillStyle(0xFFFFFF, 1);
			rowGraphics.fillRoundedRect(rowX, rowY, tableWidth, rowHeightActual, 5);
			rowGraphics.lineStyle(1, 0xE0E0E0, 1);
			rowGraphics.strokeRoundedRect(rowX, rowY, tableWidth, rowHeightActual, 5);
			this.scrollContainer.add(rowGraphics);

			// Avatar
			const avatarSize = 45;
			const avatarX = rowX + padding + avatarSize / 2;
			const avatar = this.add.circle(avatarX, y, avatarSize / 2, 0xD3D3D3);
			this.scrollContainer.add(avatar);

			// Icona faccina
			const faceIcon = this.add.text(avatarX, y, '😉', {
				fontSize: avatarSize * 0.7
			}).setOrigin(0.5);
			this.scrollContainer.add(faceIcon);

			// Nome utente
			const userName = user.nickname || 'Utente ' + user.id;
			const nameText = this.add.text(
				avatarX + avatarSize / 2 + 15,
				y,
				userName.toUpperCase(),
				{
					fontSize: 18,
					fontFamily: 'Arial',
					fill: '#333333',
					fontStyle: 'bold'
				}
			).setOrigin(0, 0.5);
			this.scrollContainer.add(nameText);

			// Bottone "Sfida" rosso
			const buttonWidth = buttonColumnWidth;
			const buttonX = rowX + tableWidth - buttonWidth / 2;

			const buttonGraphics = this.add.graphics();
			buttonGraphics.fillStyle(setup.colors.red, 1);
			buttonGraphics.fillRoundedRect(
				rowX + tableWidth - buttonWidth,
				rowY,
				buttonWidth,
				rowHeightActual,
				5
			);
			this.scrollContainer.add(buttonGraphics);

			const buttonText = this.add.text(buttonX, y, 'Sfida', {
				fontSize: 24,
				fontFamily: 'Luckiest Guy',
				fill: '#FFFFFF',
				fontStyle: 'bold'
			}).setOrigin(0.5);
			buttonText.setShadow(1, 1, '#000000', 2, true, true);
			this.scrollContainer.add(buttonText);

			// Area interattiva per il bottone (gestita separatamente per lo scroll)
			const hitArea = this.add.rectangle(
				rowX + tableWidth - buttonWidth / 2,
				y,
				buttonWidth,
				rowHeightActual,
				0x000000,
				0
			).setInteractive({ useHandCursor: true });
			this.scrollContainer.add(hitArea);

			hitArea.on('pointerup', () => {
				// Ignora il click se stiamo scrollando
				if (!this.isDragging) {
					this.createChallenge(user.id);
				}
			});
		});

		// Configura l'input per lo scroll
		this.setupScrollInput(scrollAreaTop, scrollAreaBottom, width);
	}

	/**
	 * Configura l'input per lo scroll della lista
	 *
	 * @param {number} scrollAreaTop - Coordinata Y superiore dell'area scrollabile
	 * @param {number} scrollAreaBottom - Coordinata Y inferiore dell'area scrollabile
	 * @param {number} width - Larghezza dell'area
	 */
	setupScrollInput(scrollAreaTop, scrollAreaBottom, width) {
		// Crea una zona interattiva per lo scroll
		const scrollZone = this.add.zone(
			width / 2,
			(scrollAreaTop + scrollAreaBottom) / 2,
			width,
			scrollAreaBottom - scrollAreaTop
		).setInteractive();

		// Permetti a più oggetti di ricevere eventi pointer
		// così i pulsanti nel container possono ricevere click anche sopra la scrollZone
		this.input.setTopOnly(false);

		// Variabili per tracciare il drag
		let lastPointerY = 0;
		let velocity = 0;

		scrollZone.on('pointerdown', (pointer) => {
			this.isDragging = false;
			this.dragStartY = pointer.y;
			this.scrollStartY = this.scrollY;
			lastPointerY = pointer.y;
			velocity = 0;
		});

		scrollZone.on('pointermove', (pointer) => {
			if (pointer.isDown) {
				const deltaY = pointer.y - this.dragStartY;

				// Se il movimento è significativo, considera come drag
				if (Math.abs(deltaY) > 5) {
					this.isDragging = true;
				}

				// Calcola la velocità per l'inerzia
				velocity = pointer.y - lastPointerY;
				lastPointerY = pointer.y;

				// Applica lo scroll
				this.scrollY = this.scrollStartY - deltaY;
				this.scrollY = Phaser.Math.Clamp(this.scrollY, this.minScrollY, this.maxScrollY);

				// Aggiorna la posizione del container
				this.scrollContainer.y = scrollAreaTop - this.scrollY;
			}
		});

		scrollZone.on('pointerup', () => {
			// Applica inerzia dopo il rilascio
			if (Math.abs(velocity) > 2) {
				this.tweens.add({
					targets: this,
					scrollY: Phaser.Math.Clamp(
						this.scrollY - velocity * 10,
						this.minScrollY,
						this.maxScrollY
					),
					duration: 300,
					ease: 'Power2',
					onUpdate: () => {
						this.scrollContainer.y = scrollAreaTop - this.scrollY;
					}
				});
			}

			// Reset del flag di drag dopo un breve delay
			this.time.delayedCall(50, () => {
				this.isDragging = false;
			});
		});

		// Supporto scroll con rotella del mouse
		this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY) => {
			// Verifica che il puntatore sia nell'area di scroll
			if (pointer.y >= scrollAreaTop && pointer.y <= scrollAreaBottom) {
				this.scrollY += deltaY * 0.5;
				this.scrollY = Phaser.Math.Clamp(this.scrollY, this.minScrollY, this.maxScrollY);
				this.scrollContainer.y = scrollAreaTop - this.scrollY;
			}
		});
	}

	createChallenge(friendId) {
		const roundId = runtime.challengeRoundId;
		if (!roundId) {
			this.showError('Round non trovato');
			return;
		}

		// Mostra messaggio di caricamento
		if (this.loadingText) {
			this.loadingText.destroy();
		}
		this.loadingText = this.add.text(
			this.sys.game.config.width / 2,
			this.sys.game.config.height / 2,
			'Creazione sfida...',
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FFFFFF'}
		).setOrigin(0.5);

		axios.post('/api/invites', {
			round_id: roundId,
			friend_id: friendId
		}).then((response) => {
			console.log('Sfida creata:', response.data);
			this.loadingText.destroy();

			// Verifica se il risultato è immediato (amico ha già giocato)
			if (response.data.immediate_result) {
				// Mostra direttamente il risultato
				this.scene.start('ChallengeResult', { invite: response.data.invite, result: response.data.result });
			} else {
				// Sfida creata, torna alla scena Challenge
				this.add.text(
					this.sys.game.config.width / 2,
					this.sys.game.config.height / 2,
					'Sfida inviata!',
					{...setup.styles.gameOver.greetingStyle, fontSize: 32, fill: '#4CAF50'}
				).setOrigin(0.5);

				this.time.delayedCall(2000, () => {
					this.scene.start('Challenge');
				});
			}
		}).catch((error) => {
			console.error('Errore creazione sfida:', error);
			console.error('Dettagli errore:', error.response);
			this.loadingText.destroy();

			let errorMsg = 'Errore nella creazione della sfida';
			if (error.response) {
				if (error.response.data && error.response.data.message) {
					errorMsg = error.response.data.message;
				} else if (error.response.data && typeof error.response.data === 'string') {
					errorMsg = error.response.data;
				} else if (error.response.status === 400) {
					errorMsg = 'Dati non validi. Verifica che il round sia completato.';
				} else if (error.response.status === 404) {
					errorMsg = 'Round o utente non trovato.';
				} else if (error.response.status === 401) {
					errorMsg = 'Non autenticato. Ricarica la pagina.';
				}
			}

			this.showError(errorMsg);
		});
	}

	showError(message) {
		if (this.errorText) {
			this.errorText.destroy();
		}
		this.errorText = this.add.text(
			this.sys.game.config.width / 2,
			this.sys.game.config.height / 2,
			message,
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FF0000'}
		).setOrigin(0.5);
	}

	createInviteButton(centerX) {
		// Posiziona il bottone dopo il ditloide, prima della lista amici
		const y = HEADER_HEIGHT + 30;

		const inviteButton = new Button(
			this,
			centerX,
			y + 30,
			'blue-button',
			1,
			'Invita un amico',
			false
		);

		inviteButton.on('pointerup', () => {
			this.createShareableChallenge();
		});
	}

	createShareableChallenge() {
		const roundId = runtime.challengeRoundId;
		if (!roundId) {
			this.showError('Round non trovato');
			return;
		}

		// Mostra messaggio di caricamento
		if (this.loadingText) {
			this.loadingText.destroy();
		}
		this.loadingText = this.add.text(
			this.sys.game.config.width / 2,
			this.sys.game.config.height / 2,
			'Creazione link...',
			{...setup.styles.gameOver.greetingStyle, fontSize: 24, fill: '#FFFFFF'}
		).setOrigin(0.5);

		// Crea invite senza friend_id
		// Passa frontendUrl per generare il link corretto
		axios.post('/api/invites', {
			round_id: roundId,
			frontendUrl: window.location.origin
			// friend_id non fornito
		}).then((response) => {
			console.log('Link condivisibile creato:', response.data);
			this.loadingText.destroy();

			const shareUrl = response.data.share_url;
			if (!shareUrl) {
				this.showError('Errore: link non generato');
				return;
			}

			// Condividi o copia link
			shareUtils.shareOrCopy(
				shareUrl,
				'Sfidami a Tanaca!',
				'A colpi di libri, film o canzoni. Accetta la sfida!'
			).then((result) => {
				if (result.success) {
					// Mostra feedback positivo
					const message = result.method === 'share'
						? 'Condivisione avviata!'
						: 'Link copiato!';

					const toast = this.add.text(
						this.sys.game.config.width / 2,
						this.sys.game.config.height / 2 - 100,
						message,
						{
							...setup.styles.gameOver.greetingStyle,
							fontSize: 28,
							fill: result.method === 'share' ? '#4A90E2' : '#4CAF50'
						}
					).setOrigin(0.5);

					// Rimuovi toast dopo 2 secondi
					this.time.delayedCall(2000, () => {
						toast.destroy();
					});
				} else {
					this.showError('Errore durante la condivisione');
				}
			}).catch((error) => {
				console.error('Errore condivisione:', error);
				this.showError('Errore durante la condivisione');
			});

		}).catch((error) => {
			console.error('Errore creazione link condivisibile:', error);
			this.loadingText.destroy();

			let errorMsg = 'Errore nella creazione del link';
			if (error.response) {
				if (error.response.data && error.response.data.message) {
					errorMsg = error.response.data.message;
				} else if (error.response.status === 400) {
					errorMsg = 'Dati non validi. Verifica che il round sia completato.';
				} else if (error.response.status === 401) {
					errorMsg = 'Non autenticato. Ricarica la pagina.';
				}
			}

			this.showError(errorMsg);
		});
	}
}
